import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Copy, Printer, Sparkles, ShieldAlert, Stethoscope, Heart, ClipboardList, MessageSquare, TrendingUp, HeartPulse, Target } from "lucide-react";
import { toast } from "sonner";
import { staffApi, formatApiError } from "@/lib/api";

// One-tap Claude Haiku briefing modal (Feb 2026 refactor).
//
// New section order (per operator spec):
//   1. Clinical Highlights          — merged medical + risk
//   2. Patient Personality          — verbatim internal notes OR summary
//   3. Today's Direct Briefing      — reception's note, VERBATIM
//   4. Top 3 Treatments             — most-purchased history
//   5. Medical Questionnaire        — yes-answer highlights
//   6. Patient Profile              — name / gender / DOB / contact
//
// Copy → clipboard as plain text. Print → hidden iframe with print CSS.
export default function AIBriefingModal({
  open,
  onOpenChange,
  customerId,
  customerName,
  serviceId = null,
  serviceName = "",
  // Feb 2026 — Today's appointment id so the backend can fetch the
  // reception's verbatim note. Optional; when absent the "Today's
  // Direct Briefing" section renders empty.
  appointmentId = null,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const printRef = useRef(null);
  const lastFetchKey = useRef(null);
  const inFlight = useRef(false);

  const fetchBriefing = useCallback(async () => {
    if (!customerId) return;
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setData(null);
    try {
      const params = {};
      if (serviceId) params.service_id = serviceId;
      if (appointmentId) params.appointment_id = appointmentId;
      const res = await staffApi.post(`/staff/briefing/${customerId}`, null, { params });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
      onOpenChange(false);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [customerId, serviceId, appointmentId, onOpenChange]);

  useEffect(() => {
    if (!open) {
      lastFetchKey.current = null;
      return;
    }
    const key = `${customerId}::${serviceId || ""}::${appointmentId || ""}`;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    fetchBriefing();
  }, [open, customerId, serviceId, appointmentId, fetchBriefing]);

  const regenerate = () => {
    lastFetchKey.current = null;
    fetchBriefing();
    lastFetchKey.current = `${customerId}::${serviceId || ""}::${appointmentId || ""}`;
  };

  const buildPlainText = () => {
    if (!data) return "";
    const lines = [];
    lines.push(`AI Briefing — ${customerName || "Guest"}`);
    if (serviceName) lines.push(`Today: ${serviceName}`);
    lines.push("");
    if (data.summary) { lines.push("SUMMARY"); lines.push(data.summary); lines.push(""); }
    const section = (title, items) => {
      if (!Array.isArray(items) || items.length === 0) return;
      lines.push(title.toUpperCase());
      items.forEach((i) => lines.push(`• ${i}`));
      lines.push("");
    };
    section("Clinical highlights", [...(data.clinical_highlights || []), ...(data.risk_flags || [])]);
    // Patient personality — either the summary string OR the verbatim notes
    if (data.personality_summary) {
      lines.push("PATIENT PERSONALITY");
      lines.push(data.personality_summary);
      lines.push("");
    } else if ((data.internal_notes_verbatim || []).length) {
      lines.push("PATIENT PERSONALITY (internal notes)");
      data.internal_notes_verbatim.forEach((n) => {
        lines.push(`• ${n.date}${n.service_name ? ` — ${n.service_name}` : ""}: ${n.text}`);
      });
      lines.push("");
    }
    if (data.todays_reception_note) {
      lines.push("TODAY'S DIRECT BRIEFING");
      lines.push(data.todays_reception_note);
      lines.push("");
    }
    section("Top 3 treatments", data.patient_treatments);
    section("Medical questionnaire highlights", data.medical_questionnaire_highlights);
    section("Wellness alerts (massage card)", data.wellness_alerts);
    section(
      "G.O.A.L history",
      (data.goal_history || []).map((g) => `${g.title} — ${g.status === "resolved" ? "Resolved" : "Unresolved"}`),
    );
    section("Guest preferences", data.preferences);
    return lines.join("\n");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      toast.success("Briefing copied to clipboard");
    } catch {
      toast.error("Copy failed — please select and copy manually");
    }
  };

  const printBriefing = () => {
    if (!data) return;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0"; iframe.style.bottom = "0";
    iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const listHtml = (items) => (items || []).map((i) => `<li>${escapeHtml(i)}</li>`).join("");
    const notesHtml = (data.internal_notes_verbatim || []).map((n) =>
      `<li><span class="date">${escapeHtml(n.date)}${n.service_name ? ` — ${escapeHtml(n.service_name)}` : ""}</span>: ${escapeHtml(n.text)}</li>`
    ).join("");
    const clinical = [...(data.clinical_highlights || []), ...(data.risk_flags || [])];
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>AI Briefing — ${escapeHtml(customerName || "Guest")}</title>
      <style>
        body{font-family:Manrope,Arial,sans-serif;color:#2C2D28;padding:24px;max-width:640px;margin:0 auto;}
        h1{font-family:'Cormorant Garamond',Georgia,serif;font-size:26px;margin:0 0 4px;}
        .sub{color:#728971;font-size:11px;letter-spacing:.18em;text-transform:uppercase;margin-bottom:16px;}
        h2{font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:#728971;margin:18px 0 6px;border-bottom:1px solid #E8E3DD;padding-bottom:4px;}
        ul,ol{margin:0;padding-left:18px;} li{margin-bottom:4px;font-size:13px;line-height:1.5;}
        .summary{font-size:14px;line-height:1.6;background:#F5F1EB;padding:12px 14px;border-radius:8px;margin:0 0 12px;}
        .verbatim{font-size:13px;line-height:1.6;background:#FFF8E7;padding:12px 14px;border-radius:8px;border-left:4px solid #E8B84D;}
        .date{color:#728971;font-weight:600;}
        .foot{margin-top:24px;font-size:10px;color:#a4a59f;border-top:1px solid #E8E3DD;padding-top:8px;}
      </style></head><body>
      <h1>${escapeHtml(customerName || "Guest")}</h1>
      <div class="sub">AI Briefing${serviceName ? ` · Today: ${escapeHtml(serviceName)}` : ""}</div>
      ${data.summary ? `<div class="summary">${escapeHtml(data.summary)}</div>` : ""}
      ${clinical.length ? `<h2>Clinical Highlights</h2><ul>${listHtml(clinical)}</ul>` : ""}
      ${data.personality_summary ? `<h2>Patient Personality</h2><div>${escapeHtml(data.personality_summary)}</div>` : (notesHtml ? `<h2>Patient Personality — Internal Notes</h2><ul>${notesHtml}</ul>` : "")}
      ${data.todays_reception_note ? `<h2>Today's Direct Briefing</h2><div class="verbatim">${escapeHtml(data.todays_reception_note)}</div>` : ""}
      ${data.patient_treatments?.length ? `<h2>Top 3 Treatments</h2><ol>${listHtml(data.patient_treatments)}</ol>` : ""}
      ${data.medical_questionnaire_highlights?.length ? `<h2>Medical Questionnaire Highlights</h2><ul>${listHtml(data.medical_questionnaire_highlights)}</ul>` : ""}
      ${data.wellness_alerts?.length ? `<h2 style="color:#0f766e;border-bottom-color:#5eead4;">Wellness Alerts</h2><ul style="color:#134e4a;">${listHtml(data.wellness_alerts)}</ul>` : ""}
      ${data.goal_history?.length ? `<h2>G.O.A.L History</h2><ul>${listHtml(data.goal_history.map((g) => `${g.title} — ${g.status === "resolved" ? "Resolved" : "Unresolved"}`))}</ul>` : ""}
      <div class="foot">Generated ${new Date(data.generated_at || Date.now()).toLocaleString()} · Cocoon Medical Spa</div>
      </body></html>`);
    doc.close();
    iframe.onload = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* noop */ }
      setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 500);
    };
  };

  const clinicalMerged = data ? [...(data.clinical_highlights || []), ...(data.risk_flags || [])] : [];
  const notesCount = data?.internal_notes_count || 0;
  const showPersonalitySummary = !!data?.personality_summary && notesCount > 5;
  const showVerbatimNotes = !showPersonalitySummary && (data?.internal_notes_verbatim || []).length > 0;
  const prof = data?.patient_profile || {};
  const identityBits = [
    prof.gender ? prof.gender[0].toUpperCase() + prof.gender.slice(1) : "",
    prof.age != null && prof.age !== "" ? `${prof.age} yrs` : "",
    prof.nationality || "",
  ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="ai-briefing-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            AI Briefing — {customerName || "Guest"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Claude briefing organised into Clinical Highlights, Patient Personality, Today&apos;s Direct Briefing, Top Treatments, Medical Questionnaire Highlights, and Patient Profile.
          </DialogDescription>
          {/* Jun 2026 — identity line directly under the guest's name:
              gender · age · nationality, so the clinician can greet and
              address the right person without leaving the briefing. */}
          {identityBits.length > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="ai-briefing-identity">
              {identityBits.join(" · ")}
            </div>
          )}
          {serviceName && (
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Today: {serviceName}
            </div>
          )}
        </DialogHeader>

        <div ref={printRef} className="space-y-4">
          {loading && (
            <div
              className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3"
              data-testid="ai-briefing-loading"
            >
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              <div className="text-sm">Reading medical profile, internal notes and recent visits…</div>
            </div>
          )}

          {!loading && data && (
            <>
              {data.summary && (
                <div className="rounded-lg bg-[#F5F1EB] px-4 py-3 text-sm leading-relaxed" data-testid="ai-briefing-summary">
                  {data.summary}
                </div>
              )}

              {/* 1. Clinical Highlights (medical + risk merged) */}
              <BriefingSection
                icon={<Stethoscope className="w-4 h-4 text-emerald-700" />}
                title="Clinical Highlights"
                items={clinicalMerged}
                emptyLabel="Medical profile is blank"
                testid="ai-briefing-clinical"
              />

              {/* 2. Patient Personality — verbatim notes OR distilled summary */}
              <div data-testid="ai-briefing-personality">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                  <MessageSquare className="w-4 h-4 text-indigo-600" />
                  <span>Patient Personality (Internal Notes)</span>
                  {notesCount > 0 && (
                    <span className="ml-auto text-[10px] normal-case tracking-normal text-muted-foreground/80" data-testid="ai-briefing-personality-count">
                      {notesCount} note{notesCount === 1 ? "" : "s"} on file
                    </span>
                  )}
                </div>
                {showPersonalitySummary ? (
                  <div className="text-sm italic text-indigo-900 bg-indigo-50/60 border border-indigo-100 rounded-md px-3 py-2" data-testid="ai-briefing-personality-summary">
                    {data.personality_summary}
                  </div>
                ) : showVerbatimNotes ? (
                  <ul className="space-y-2 text-sm">
                    {(data.internal_notes_verbatim || []).map((n, idx) => (
                      <li key={idx} className="border-l-2 border-indigo-200 pl-3" data-testid={`ai-briefing-personality-note-${idx}`}>
                        <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">
                          {n.date}{n.service_name ? ` · ${n.service_name}` : ""}
                        </div>
                        <div className="text-neutral-800 whitespace-pre-wrap">{n.text}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-muted-foreground italic pl-6">No internal notes on file yet</div>
                )}
              </div>

              {/* 3. Today's Direct Briefing — reception's verbatim note */}
              <div data-testid="ai-briefing-todays">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                  <ClipboardList className="w-4 h-4 text-amber-600" />
                  <span>Today&apos;s Direct Briefing</span>
                </div>
                {data.todays_reception_note ? (
                  <div className="text-sm rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 whitespace-pre-wrap" data-testid="ai-briefing-todays-note">
                    {data.todays_reception_note}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic pl-6">Reception hasn&apos;t added a note for today&apos;s visit</div>
                )}
              </div>

              {/* 4. Top 3 Treatments */}
              <BriefingSection
                icon={<TrendingUp className="w-4 h-4 text-sky-600" />}
                title="Top 3 Treatments"
                items={data.patient_treatments}
                ordered
                emptyLabel="No history yet — first-time guest"
                testid="ai-briefing-top-treatments"
              />

              {/* 5. Medical Questionnaire Highlights */}
              <BriefingSection
                icon={<ShieldAlert className="w-4 h-4 text-rose-600" />}
                title="Medical Questionnaire Highlights"
                items={data.medical_questionnaire_highlights}
                emptyLabel="No 'yes' answers on file"
                testid="ai-briefing-mq-highlights"
              />

              {/* 6. Wellness Alerts (Massage Preference Card) — distinct
                  teal palette so the therapist reads this block as a
                  standalone wellness signal, not a clinical highlight. */}
              {(data.wellness_alerts?.length || data.massage_preferences?.pressure) ? (
                <div data-testid="ai-briefing-wellness">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-teal-800 mb-1.5">
                    <HeartPulse className="w-4 h-4 text-teal-600" />
                    <span>Wellness Alerts</span>
                    {data.massage_preferences?.submitted_at && (
                      <span className="ml-auto text-[10px] normal-case tracking-normal text-teal-700/80" data-testid="ai-briefing-wellness-submitted">
                        Card submitted {new Date(data.massage_preferences.submitted_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="rounded-md border border-teal-200 bg-teal-50/70 px-3 py-2" data-testid="ai-briefing-wellness-block">
                    {(data.wellness_alerts || []).length ? (
                      <ul className="list-disc pl-5 space-y-1 text-sm text-teal-900">
                        {data.wellness_alerts.map((a, i) => (
                          <li key={i} data-testid={`ai-briefing-wellness-alert-${i}`}>{a}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-xs italic text-teal-800/70">No massage card on file for today.</div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* G.O.A.L history (Jun 2026) — this client's past concerns,
                  title + status only, so the care team knows who needs
                  extra attention. Only rendered for a specific client. */}
              {(data.goal_history || []).length > 0 && (
                <div data-testid="ai-briefing-goal">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                    <Target className="w-4 h-4 text-amber-600" />
                    <span>G.O.A.L History</span>
                    <span className="ml-auto text-[10px] normal-case tracking-normal text-muted-foreground/80" data-testid="ai-briefing-goal-count">
                      {data.goal_history.length} record{data.goal_history.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {data.goal_history.map((g, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm rounded-md border border-amber-100 bg-amber-50/60 px-3 py-1.5"
                        data-testid={`ai-briefing-goal-item-${i}`}
                      >
                        <span className="flex-1 min-w-0 truncate text-amber-950">{g.title}</span>
                        <span
                          className={
                            "text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border " +
                            (g.status === "resolved"
                              ? "text-emerald-800 bg-emerald-50 border-emerald-200"
                              : "text-rose-800 bg-rose-50 border-rose-200")
                          }
                        >
                          {g.status === "resolved" ? "Resolved" : "Unresolved"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Guest preferences (chair, drink, do-not-mention) */}
              <BriefingSection
                icon={<Heart className="w-4 h-4 text-pink-500" />}
                title="Guest Preferences"
                items={data.preferences}
                emptyLabel="No non-alert preferences on file"
                testid="ai-briefing-preferences"
              />

              <div className="pt-2 text-[10px] text-muted-foreground uppercase tracking-wider text-right" data-testid="ai-briefing-timestamp">
                Generated {new Date(data.generated_at || Date.now()).toLocaleTimeString()}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button variant="outline" size="sm" onClick={regenerate} disabled={loading} data-testid="ai-briefing-regenerate">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate
          </Button>
          <Button variant="outline" size="sm" onClick={copyToClipboard} disabled={loading || !data} data-testid="ai-briefing-copy">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
          </Button>
          <Button size="sm" onClick={printBriefing} disabled={loading || !data} data-testid="ai-briefing-print">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BriefingSection({ icon, title, items, emptyLabel, testid, ordered = false }) {
  const list = Array.isArray(items) ? items : [];
  const ListTag = ordered ? "ol" : "ul";
  const listStyle = ordered ? "list-decimal pl-8 space-y-1 text-sm" : "list-disc pl-8 space-y-1 text-sm";
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
        {icon}<span>{title}</span>
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-muted-foreground italic pl-6">{emptyLabel}</div>
      ) : (
        <ListTag className={listStyle}>
          {list.map((i, idx) => (
            <li key={idx}>{i}</li>
          ))}
        </ListTag>
      )}
    </div>
  );
}

function ProfileSection(_props) {
  // Retained for backwards-compat with imports elsewhere. The Patient
  // Profile section was removed from the AI Briefing (Feb 2026) per
  // operator request — profile details belong on the customer detail
  // page, not on the treating clinician's briefing.
  return null;
}

// Minimal HTML entity escape — used only inside the print-iframe writer
// where we hand-craft the markup.
function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
